import { logger } from "./logger";

interface UTMParams {
	utm_source?: string;
	utm_medium?: string;
	utm_campaign?: string;
	utm_term?: string;
	utm_content?: string;
}

interface UTMStorage {
	params: UTMParams;
	timestamp: number;
}

const UTM_STORAGE_KEY = "settlin_utm_params";
const UTM_EXPIRY_HOURS = 24;

export const utmUtils = {
	/**
	 * Extract UTM parameters from URL search params
	 */
	extractFromURL: (url?: string): UTMParams => {
		const targetUrl = url || (typeof window !== "undefined" ? window.location.href : undefined);

		if (!targetUrl) return {};

		try {
			const urlObj = new URL(targetUrl);
			const searchParams = urlObj.searchParams;

			const utmParams: UTMParams = {};

			if (searchParams.has("utm_source")) {
				utmParams.utm_source = searchParams.get("utm_source") || undefined;
			}
			if (searchParams.has("utm_medium")) {
				utmParams.utm_medium = searchParams.get("utm_medium") || undefined;
			}
			if (searchParams.has("utm_campaign")) {
				utmParams.utm_campaign = searchParams.get("utm_campaign") || undefined;
			}
			if (searchParams.has("utm_term")) {
				utmParams.utm_term = searchParams.get("utm_term") || undefined;
			}
			if (searchParams.has("utm_content")) {
				utmParams.utm_content = searchParams.get("utm_content") || undefined;
			}

			return utmParams;
		} catch (error) {
			logger.warn("Failed to extract UTM params from URL:", error);
			return {};
		}
	},

	/**
	 * Save UTM parameters to localStorage with timestamp
	 */
	save: (params: UTMParams): void => {
		if (typeof window === "undefined") return;

		// Only save if there are actual UTM parameters
		if (Object.keys(params).length === 0) return;

		const storage: UTMStorage = {
			params,
			timestamp: Date.now(),
		};

		try {
			localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(storage));
		} catch (error) {
			logger.warn("Failed to save UTM parameters to localStorage:", error);
		}
	},

	/**
	 * Load UTM parameters from localStorage
	 */
	load: (): UTMParams | null => {
		if (typeof window === "undefined") return null;

		try {
			const stored = localStorage.getItem(UTM_STORAGE_KEY);
			if (!stored) return null;

			const storage: UTMStorage = JSON.parse(stored);

			// Check if data is expired (more than 24 hours old)
			const isExpired = Date.now() - storage.timestamp > UTM_EXPIRY_HOURS * 60 * 60 * 1000;

			if (isExpired) {
				utmUtils.clear();
				return null;
			}

			return storage.params;
		} catch (error) {
			logger.warn("Failed to load UTM parameters from localStorage:", error);
			utmUtils.clear();
			return null;
		}
	},

	/**
	 * Clear UTM parameters from localStorage
	 */
	clear: (): void => {
		if (typeof window === "undefined") return;

		try {
			localStorage.removeItem(UTM_STORAGE_KEY);
		} catch (error) {
			logger.warn("Failed to clear UTM parameters from localStorage:", error);
		}
	},

	/**
	 * Get current UTM parameters (from localStorage or URL)
	 */
	getCurrent: (): UTMParams => {
		// First try to get from localStorage
		const stored = utmUtils.load();
		if (stored) return stored;

		// If not in localStorage, try to extract from current URL
		const fromURL = utmUtils.extractFromURL();
		if (Object.keys(fromURL).length > 0) {
			utmUtils.save(fromURL);
			return fromURL;
		}

		return {};
	},

	/**
	 * Check if UTM parameters exist and are valid
	 */
	hasValidParams: (): boolean => {
		const params = utmUtils.getCurrent();
		return Object.keys(params).length > 0;
	},
};
